import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PromoPickerDialog } from "@/components/promotions/PromoPickerDialog";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RebrandImageTab from "@/components/promotions/RebrandImageTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  Plus,
  Percent,
  Tag,
  Gift,
  Package,
  Calendar,
  Search,
  Edit,
  Trash2,
  Power,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Clock,
  CheckCircle,
  XCircle,
  Copy,
  Printer,
  AlertTriangle,
  Image as ImageIcon,
  Sparkles,
  Calculator,
} from "lucide-react";
import { toast } from "sonner";
import VoucherGiveawayCalculator from "@/components/promotions/VoucherGiveawayCalculator";

const API = process.env.REACT_APP_BACKEND_URL;

// Promotion type icons and labels
const PROMO_TYPES = {
  percentage: { icon: Percent, label: "Percentage Off", color: "bg-blue-100 text-blue-800" },
  fixed_amount: { icon: DollarSign, label: "Fixed Amount Off", color: "bg-green-100 text-green-800" },
  custom_price: { icon: Tag, label: "Custom Price", color: "bg-teal-100 text-teal-800" },
  buy_x_get_y: { icon: Gift, label: "Buy X Get Y", color: "bg-purple-100 text-purple-800" },
  bundle: { icon: Package, label: "Bundle Deal", color: "bg-orange-100 text-orange-800" },
  multi_buy: { icon: ShoppingCart, label: "Multi-Buy", color: "bg-pink-100 text-pink-800" },
  // "Spend R500, get a free R50 airtime voucher" style rewards.
  // Modelled + priced via the Voucher Calculator tab.
  spend_reward: { icon: Gift, label: "Spend Reward (Voucher)", color: "bg-amber-100 text-amber-800" },
};

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", 
  "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280", "#000000"
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', { 
    style: 'currency', 
    currency: 'ZAR',
    minimumFractionDigits: 2 
  }).format(value || 0);
};

// Always return cost price inclusive of VAT using cost_price_excl_vat * (1 + vat_rate/100)
const getCostPriceInclVat = (product) => {
  const excl = product.cost_price_excl_vat || 0;
  const vatRate = product.vat_rate || 0;
  if (excl > 0) {
    return Math.round(excl * (1 + vatRate / 100) * 100) / 100;
  }
  // fallback to stored incl price if set
  if (product.cost_price_incl_vat > 0) return product.cost_price_incl_vat;
  // last resort: stored cost_price (shown as-is)
  return product.cost_price || 0;
};

export default function Promotions() {
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarData, setCalendarData] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [categories, setCategories] = useState([]);
  // Stock departments (child of category) — allowed exclusion granularity
  // for spend-reward promos.
  const [departments, setDepartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  
  // Form state
  const [form, setForm] = useState({
    name: "",
    description: "",
    promotion_type: "percentage",
    discount_percentage: "",
    discount_amount: "",
    buy_quantity: "2",
    get_quantity: "1",
    get_discount_percent: "100",
    bundle_price: "",
    multi_buy_quantity: "3",
    multi_buy_price: "",
    applies_to: "specific",
    category_ids: [],
    product_ids: [],
    minimum_purchase: "",
    maximum_uses: "",
    max_per_transaction: "",
    customer_groups: ["retail", "trade"],
    start_date: new Date().toISOString().split("T")[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    active_days: [0, 1, 2, 3, 4, 5, 6],
    start_time: "00:00",
    end_time: "23:59",
    shelf_talker_text: "",
    highlight_color: "#ef4444",
    show_original_price: true,
    is_active: true,
    priority: 0,
    // Spend-reward
    spend_threshold: "",
    threshold_is_vat_inclusive: true,
    voucher_value: "",
    voucher_cost: "",
    voucher_description: "",
    voucher_expiry_days: "30",
    qualifying_min_markup_percent: "",
    excluded_category_ids: [],
    excluded_department_ids: [],
  });

  useEffect(() => {
    loadPromotions();
    loadCategories();
  }, []);

  useEffect(() => {
    if (activeTab === "calendar") {
      loadCalendar();
    } else if (activeTab === "performance") {
      loadPerformance();
    }
  }, [activeTab, calendarYear, calendarMonth]);

  const loadPromotions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/promotions?include_expired=true`);
      setPromotions(res.data);
    } catch (error) {
      console.error("Error loading promotions:", error);
      toast.error("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    // Uses the single endpoint that returns BOTH categories and departments
    // so the spend-reward promo editor can offer either level of granularity.
    try {
      const res = await axios.get(`${API}/api/categories-departments`);
      setCategories(res.data.categories || []);
      setDepartments(res.data.departments || []);
    } catch (error) {
      console.error("Error loading categories/departments:", error);
    }
  };

  const loadCalendar = async () => {
    try {
      const res = await axios.get(`${API}/api/promotions/calendar/${calendarYear}/${calendarMonth}`);
      setCalendarData(res.data);
    } catch (error) {
      console.error("Error loading calendar:", error);
    }
  };

  const loadPerformance = async () => {
    try {
      const res = await axios.get(`${API}/api/promotions/performance`);
      setPerformance(res.data);
    } catch (error) {
      console.error("Error loading performance:", error);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setProducts([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/products/search?q=${encodeURIComponent(query)}&limit=20`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error searching products:", error);
    }
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error("Please enter a promotion name");
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error("Please set start and end dates");
      return;
    }
    
    // Validate custom_price type has products with prices
    if (form.promotion_type === "custom_price") {
      if (selectedProducts.length === 0) {
        toast.error("Please add at least one product for custom price promotion");
        return;
      }
      const missingPrices = selectedProducts.filter(p => !p.promo_price || parseFloat(p.promo_price) <= 0);
      if (missingPrices.length > 0) {
        toast.error("Please enter promo prices for all products");
        return;
      }
    }

    try {
      // Build promo_products array for custom_price type
      const promoProducts = form.promotion_type === "custom_price" 
        ? selectedProducts.map(p => ({
            product_id: p.id,
            plu_code: p.plu_code,
            description: p.description || p.name,
            original_price: p.selling_price || p.selling_price_incl_vat || 0,
            cost_price: getCostPriceInclVat(p),
            promo_price: parseFloat(p.promo_price)
          }))
        : [];
      
      const payload = {
        ...form,
        discount_percentage: form.discount_percentage ? parseFloat(form.discount_percentage) : null,
        discount_amount: form.discount_amount ? parseFloat(form.discount_amount) : null,
        buy_quantity: form.buy_quantity ? parseInt(form.buy_quantity) : null,
        get_quantity: form.get_quantity ? parseInt(form.get_quantity) : null,
        get_discount_percent: form.get_discount_percent ? parseFloat(form.get_discount_percent) : 100,
        bundle_price: form.bundle_price ? parseFloat(form.bundle_price) : null,
        multi_buy_quantity: form.multi_buy_quantity ? parseInt(form.multi_buy_quantity) : null,
        multi_buy_price: form.multi_buy_price ? parseFloat(form.multi_buy_price) : null,
        minimum_purchase: form.minimum_purchase ? parseFloat(form.minimum_purchase) : null,
        maximum_uses: form.maximum_uses ? parseInt(form.maximum_uses) : null,
        max_per_transaction: form.max_per_transaction ? parseInt(form.max_per_transaction) : null,
        priority: parseInt(form.priority) || 0,
        product_ids: selectedProducts.map(p => p.id),
        promo_products: promoProducts,
        // Spend-reward: coerce empty strings to null so the backend
        // doesn't reject with "cannot cast '' to float".
        spend_threshold: form.spend_threshold ? parseFloat(form.spend_threshold) : null,
        voucher_value: form.voucher_value ? parseFloat(form.voucher_value) : null,
        voucher_cost: form.voucher_cost ? parseFloat(form.voucher_cost) : null,
        voucher_expiry_days: form.voucher_expiry_days ? parseInt(form.voucher_expiry_days, 10) : 30,
        qualifying_min_markup_percent: form.qualifying_min_markup_percent
          ? parseFloat(form.qualifying_min_markup_percent)
          : null,
        excluded_category_ids: form.excluded_category_ids || [],
        excluded_department_ids: form.excluded_department_ids || [],
      };

      if (editingPromo) {
        await saveWithRetry(() => axios.put(`${API}/api/promotions/${editingPromo.id}`, payload));
        toast.success("Promotion updated");
      } else {
        await saveWithRetry(() => axios.post(`${API}/api/promotions`, payload));
        toast.success("Promotion created");
      }

      setShowModal(false);
      resetForm();
      loadPromotions();
    } catch (error) {
      console.error("Error saving promotion:", error);
      toast.error(errorMessage(error, "Failed to save promotion"));
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setForm({
      ...promo,
      discount_percentage: promo.discount_percentage?.toString() || "",
      discount_amount: promo.discount_amount?.toString() || "",
      buy_quantity: promo.buy_quantity?.toString() || "2",
      get_quantity: promo.get_quantity?.toString() || "1",
      get_discount_percent: promo.get_discount_percent?.toString() || "100",
      bundle_price: promo.bundle_price?.toString() || "",
      multi_buy_quantity: promo.multi_buy_quantity?.toString() || "3",
      multi_buy_price: promo.multi_buy_price?.toString() || "",
      minimum_purchase: promo.minimum_purchase?.toString() || "",
      maximum_uses: promo.maximum_uses?.toString() || "",
      max_per_transaction: promo.max_per_transaction?.toString() || "",
      priority: promo.priority?.toString() || "0",
      // Spend reward
      spend_threshold: promo.spend_threshold?.toString() || "",
      threshold_is_vat_inclusive: promo.threshold_is_vat_inclusive !== false,
      voucher_value: promo.voucher_value?.toString() || "",
      voucher_cost: promo.voucher_cost?.toString() || "",
      voucher_description: promo.voucher_description || "",
      voucher_expiry_days: promo.voucher_expiry_days?.toString() || "30",
      qualifying_min_markup_percent: promo.qualifying_min_markup_percent?.toString() || "",
      excluded_category_ids: promo.excluded_category_ids || [],
      excluded_department_ids: promo.excluded_department_ids || [],
    });
    // Load promo_products for custom_price type
    if (promo.promotion_type === "custom_price" && promo.promo_products?.length > 0) {
      const productsWithPrices = promo.promo_products.map(pp => ({
        id: pp.product_id,
        plu_code: pp.plu_code,
        description: pp.description,
        selling_price: pp.original_price,
        cost_price: pp.cost_price || 0,
        promo_price: pp.promo_price?.toString() || ""
      }));
      setSelectedProducts(productsWithPrices);
    } else {
      setSelectedProducts([]);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this promotion?")) return;
    try {
      await saveWithRetry(() => axios.delete(`${API}/api/promotions/${id}`));
      toast.success("Promotion deleted");
      loadPromotions();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to delete promotion"));
    }
  };

  const handlePrintPromoList = (promo) => {
    // Get products from promo_products or try to load from product_ids
    const products = promo.promo_products || [];
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Promotion: ${promo.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            font-size: 12px;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 { 
            font-size: 24px; 
            margin-bottom: 5px;
            color: #333;
          }
          .header .subtitle {
            font-size: 14px;
            color: #666;
          }
          .promo-info {
            background: #f5f5f5;
            padding: 10px;
            margin-bottom: 15px;
            border-radius: 5px;
          }
          .promo-info p {
            margin: 3px 0;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
          }
          th { 
            background-color: #4a5568; 
            color: white;
            font-weight: bold;
          }
          tr:nth-child(even) { background-color: #f9f9f9; }
          tr:hover { background-color: #f1f1f1; }
          .price { text-align: right; font-family: monospace; }
          .promo-price { 
            color: #e53e3e; 
            font-weight: bold; 
          }
          .savings {
            color: #38a169;
            font-weight: bold;
          }
          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏷️ ${promo.name}</h1>
          <div class="subtitle">${promo.description || 'Promotional Price List'}</div>
        </div>
        
        <div class="promo-info">
          <p><strong>Valid:</strong> ${new Date(promo.start_date).toLocaleDateString()} - ${new Date(promo.end_date).toLocaleDateString()}</p>
          <p><strong>Type:</strong> ${promo.promotion_type === 'custom_price' ? 'Custom Prices' : promo.promotion_type}</p>
          <p><strong>Total Products:</strong> ${products.length}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 60px;">#</th>
              <th style="width: 100px;">PLU</th>
              <th>Product Description</th>
              <th style="width: 100px;" class="price">Cost</th>
              <th style="width: 100px;" class="price">Normal</th>
              <th style="width: 100px;" class="price">Promo</th>
              <th style="width: 80px;" class="price">Savings</th>
            </tr>
          </thead>
          <tbody>
            ${products.map((p, idx) => {
              const normalPrice = p.original_price || 0;
              const promoPrice = p.promo_price || 0;
              const savings = normalPrice > 0 ? ((normalPrice - promoPrice) / normalPrice * 100).toFixed(0) : 0;
              return `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${p.plu_code || '-'}</td>
                  <td>${p.description || p.name || '-'}</td>
                  <td class="price">R${(p.cost_price || 0).toFixed(2)}</td>
                  <td class="price">R${normalPrice.toFixed(2)}</td>
                  <td class="price promo-price">R${promoPrice.toFixed(2)}</td>
                  <td class="price savings">${savings}%</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Printed on ${new Date().toLocaleString()} | Prices valid during promotion period only</p>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.post(`${API}/api/promotions/${id}/toggle`);
      toast.success(res.data.is_active ? "Promotion activated" : "Promotion deactivated");
      loadPromotions();
    } catch (error) {
      toast.error("Failed to toggle promotion");
    }
  };

  const resetForm = () => {
    setEditingPromo(null);
    setForm({
      name: "",
      description: "",
      promotion_type: "percentage",
      discount_percentage: "",
      discount_amount: "",
      buy_quantity: "2",
      get_quantity: "1",
      get_discount_percent: "100",
      bundle_price: "",
      multi_buy_quantity: "3",
      multi_buy_price: "",
      applies_to: "specific",
      category_ids: [],
      product_ids: [],
      minimum_purchase: "",
      maximum_uses: "",
      max_per_transaction: "",
      customer_groups: ["retail", "trade"],
      start_date: new Date().toISOString().split("T")[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      active_days: [0, 1, 2, 3, 4, 5, 6],
      start_time: "00:00",
      end_time: "23:59",
      shelf_talker_text: "",
      highlight_color: "#ef4444",
      show_original_price: true,
      is_active: true,
      priority: 0,
      spend_threshold: "",
      threshold_is_vat_inclusive: true,
      voucher_value: "",
      voucher_cost: "",
      voucher_description: "",
      voucher_expiry_days: "30",
      qualifying_min_markup_percent: "",
      excluded_category_ids: [],
      excluded_department_ids: [],
    });
    setSelectedProducts([]);
  };

  const addProduct = (product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, product]);
    }
    setProductSearch("");
    setProducts([]);
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const filteredPromotions = promotions.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const today = new Date().toISOString().split("T")[0];
    
    if (activeTab === "active") {
      return matchesSearch && p.is_active && p.end_date >= today;
    } else if (activeTab === "scheduled") {
      return matchesSearch && p.start_date > today;
    } else if (activeTab === "expired") {
      return matchesSearch && p.end_date < today;
    }
    return matchesSearch;
  });

  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="p-6 space-y-6" data-testid="promotions-page">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promotions</h1>
          <p className="text-gray-500">Manage discounts, deals and special offers</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate("/promo-flyers")}
            data-testid="goto-promo-flyers-btn"
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
          >
            <ImageIcon className="h-4 w-4 mr-2" />
            Promo Flyers
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/new-arrivals-promo")}
            data-testid="goto-new-arrivals-btn"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            New Arrivals Promo
          </Button>
          <Button variant="outline" onClick={() => setShowPicker(true)} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="pick-products-for-promo-btn">
            <Sparkles className="h-4 w-4 mr-2" />
            Pick Products for Promo
          </Button>
          <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="create-promotion-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Promotion
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">
            <CheckCircle className="h-4 w-4 mr-2" />
            Active ({promotions.filter(p => p.is_active && p.end_date >= new Date().toISOString().split("T")[0]).length})
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            <Clock className="h-4 w-4 mr-2" />
            Scheduled
          </TabsTrigger>
          <TabsTrigger value="expired" data-testid="tab-expired">
            <XCircle className="h-4 w-4 mr-2" />
            Expired
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Calendar className="h-4 w-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">
            <BarChart3 className="h-4 w-4 mr-2" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="rebrand" data-testid="tab-rebrand">
            <ImageIcon className="h-4 w-4 mr-2" />
            Re-Brand Image
          </TabsTrigger>
          <TabsTrigger value="calculator" data-testid="tab-calculator">
            <Calculator className="h-4 w-4 mr-2" />
            Voucher Calculator
          </TabsTrigger>
        </TabsList>

        {/* Active/Scheduled/Expired Tabs */}
        {["active", "scheduled", "expired"].includes(activeTab) && (
          <TabsContent value={activeTab} className="mt-6">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search promotions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-promotions"
                />
              </div>
            </div>

            {/* Promotions Grid */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading promotions...</div>
            ) : filteredPromotions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">No promotions found</h3>
                  <p className="text-gray-500 mt-1">Create your first promotion to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredPromotions.map((promo) => {
                  const TypeInfo = PROMO_TYPES[promo.promotion_type] || PROMO_TYPES.percentage;
                  const TypeIcon = TypeInfo.icon;
                  
                  return (
                    <Card 
                      key={promo.id} 
                      className={`relative overflow-hidden ${!promo.is_active ? 'opacity-60' : ''}`}
                      data-testid={`promotion-card-${promo.id}`}
                    >
                      {/* Color bar */}
                      <div 
                        className="absolute top-0 left-0 right-0 h-1"
                        style={{ backgroundColor: promo.highlight_color }}
                      />
                      
                      <CardContent className="pt-5">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg ${TypeInfo.color}`}>
                              <TypeIcon className="h-4 w-4" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{promo.name}</h3>
                              <Badge variant="outline" className="text-xs">
                                {TypeInfo.label}
                              </Badge>
                            </div>
                          </div>
                          <Switch
                            checked={promo.is_active}
                            onCheckedChange={() => handleToggle(promo.id)}
                            data-testid={`toggle-${promo.id}`}
                          />
                        </div>

                        {/* Discount Display */}
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          {promo.promotion_type === "percentage" && (
                            <div className="text-2xl font-bold text-green-600">
                              {promo.discount_percentage}% OFF
                            </div>
                          )}
                          {promo.promotion_type === "fixed_amount" && (
                            <div className="text-2xl font-bold text-green-600">
                              {formatCurrency(promo.discount_amount)} OFF
                            </div>
                          )}
                          {promo.promotion_type === "custom_price" && (
                            <div className="text-xl font-bold text-teal-600">
                              Custom Prices Set
                              {promo.promo_products?.length > 0 && (
                                <div className="text-sm font-normal text-gray-500 mt-1">
                                  {promo.promo_products.length} product{promo.promo_products.length > 1 ? 's' : ''}
                                </div>
                              )}
                            </div>
                          )}
                          {promo.promotion_type === "buy_x_get_y" && (
                            <div className="text-xl font-bold text-purple-600">
                              Buy {promo.buy_quantity} Get {promo.get_quantity} 
                              {promo.get_discount_percent === 100 ? " FREE" : ` ${promo.get_discount_percent}% Off`}
                            </div>
                          )}
                          {promo.promotion_type === "multi_buy" && (
                            <div className="text-xl font-bold text-pink-600">
                              {promo.multi_buy_quantity} for {formatCurrency(promo.multi_buy_price)}
                            </div>
                          )}
                          {promo.promotion_type === "bundle" && (
                            <div className="text-xl font-bold text-orange-600">
                              Bundle: {formatCurrency(promo.bundle_price)}
                            </div>
                          )}
                        </div>

                        {/* Date Range */}
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                          <Calendar className="h-4 w-4" />
                          <span>{promo.start_date} to {promo.end_date}</span>
                        </div>

                        {/* Stats */}
                        <div className="flex gap-4 text-sm text-gray-600 mb-3">
                          <div>
                            <span className="font-medium">{promo.times_used || 0}</span> uses
                          </div>
                          <div>
                            <span className="font-medium">{formatCurrency(promo.total_discount_given || 0)}</span> given
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3 border-t">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(promo)}>
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            resetForm();
                            setForm({...form, ...promo, name: `${promo.name} (Copy)`});
                            setShowModal(true);
                          }}>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </Button>
                          {promo.promo_products?.length > 0 && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                              onClick={() => handlePrintPromoList(promo)}
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              Print
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(promo.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Promotion Calendar</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => {
                    if (calendarMonth === 1) {
                      setCalendarMonth(12);
                      setCalendarYear(calendarYear - 1);
                    } else {
                      setCalendarMonth(calendarMonth - 1);
                    }
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium min-w-[150px] text-center">
                    {calendarData?.month_name} {calendarYear}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => {
                    if (calendarMonth === 12) {
                      setCalendarMonth(1);
                      setCalendarYear(calendarYear + 1);
                    } else {
                      setCalendarMonth(calendarMonth + 1);
                    }
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {calendarData && (
                <div className="grid grid-cols-7 gap-1">
                  {/* Weekday headers */}
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* Empty cells for days before month starts */}
                  {Array.from({ length: new Date(calendarYear, calendarMonth - 1, 1).getDay() }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-24 bg-gray-50 rounded" />
                  ))}
                  
                  {/* Calendar days */}
                  {calendarData.days.map(day => (
                    <div 
                      key={day.date} 
                      className={`h-24 border rounded p-1 overflow-hidden ${
                        day.date === new Date().toISOString().split("T")[0] ? 'border-blue-500 bg-blue-50' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-700">{day.day}</div>
                      <div className="space-y-1 mt-1">
                        {day.promotions.slice(0, 2).map(promo => (
                          <div 
                            key={promo.id}
                            className="text-xs px-1 py-0.5 rounded truncate text-white"
                            style={{ backgroundColor: promo.color }}
                            title={promo.name}
                          >
                            {promo.name}
                          </div>
                        ))}
                        {day.promotions.length > 2 && (
                          <div className="text-xs text-gray-500">+{day.promotions.length - 2} more</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Promotion Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Promotion</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Times Used</TableHead>
                    <TableHead className="text-right">Discount Given</TableHead>
                    <TableHead className="text-right">Sales Value</TableHead>
                    <TableHead className="text-right">ROI</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {performance.map(promo => {
                    const TypeInfo = PROMO_TYPES[promo.type] || PROMO_TYPES.percentage;
                    return (
                      <TableRow key={promo.id}>
                        <TableCell className="font-medium">{promo.name}</TableCell>
                        <TableCell>
                          <Badge className={TypeInfo.color}>{TypeInfo.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {promo.start_date} - {promo.end_date}
                        </TableCell>
                        <TableCell className="text-right">{promo.times_used}</TableCell>
                        <TableCell className="text-right text-red-600">
                          -{formatCurrency(promo.total_discount_given)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(promo.total_sales_value)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={promo.roi > 0 ? "default" : "destructive"}>
                            {promo.roi > 0 ? "+" : ""}{promo.roi}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {performance.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No promotion data yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rebrand" className="mt-6">
          <RebrandImageTab />
        </TabsContent>

        <TabsContent value="calculator" className="mt-6">
          <VoucherGiveawayCalculator />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPromo ? "Edit Promotion" : "Create New Promotion"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Promotion Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="e.g., Summer Sale 20% Off"
                  data-testid="promo-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Optional description for internal use"
                  rows={2}
                />
              </div>
            </div>

            {/* Promotion Type */}
            <div>
              <Label>Promotion Type *</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                {Object.entries(PROMO_TYPES).map(([key, { icon: Icon, label, color }]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm({...form, promotion_type: key})}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      form.promotion_type === key 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`h-6 w-6 mx-auto mb-1 ${form.promotion_type === key ? 'text-blue-600' : 'text-gray-500'}`} />
                    <div className="text-xs text-center">{label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Type-specific fields */}
            <div className="bg-gray-50 rounded-lg p-4">
              {form.promotion_type === "percentage" && (
                <div>
                  <Label>Discount Percentage *</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={form.discount_percentage}
                      onChange={(e) => setForm({...form, discount_percentage: e.target.value})}
                      className="w-32"
                      data-testid="discount-percentage-input"
                    />
                    <span className="text-gray-500">% off</span>
                  </div>
                </div>
              )}

              {form.promotion_type === "custom_price" && (
                <div className="space-y-4">
                  <div>
                    <Label>Search Product by PLU or Description *</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Enter PLU code or product name..."
                        value={productSearch}
                        onChange={(e) => {
                          setProductSearch(e.target.value);
                          searchProducts(e.target.value);
                        }}
                        className="pl-10"
                        data-testid="custom-price-search"
                      />
                      {products.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {products.map(product => (
                            <div
                              key={product.id}
                              className="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => {
                                // Add product with empty promo_price for custom_price type
                                const newProduct = {
                                  ...product,
                                  // Normalize field names for consistency — always inclusive of VAT
                                  cost_price: getCostPriceInclVat(product),
                                  selling_price: product.selling_price_incl_vat || product.selling_price || 0,
                                  promo_price: ""
                                };
                                if (!selectedProducts.find(p => p.id === product.id)) {
                                  setSelectedProducts([...selectedProducts, newProduct]);
                                  setForm({...form, product_ids: [...form.product_ids, product.id]});
                                }
                                setProductSearch("");
                                setProducts([]);
                              }}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">{product.description || product.name}</div>
                                  <div className="text-sm text-gray-500">PLU: {product.plu_code}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-gray-500">Cost: R{getCostPriceInclVat(product).toFixed(2)}</div>
                                  <div className="font-medium text-green-600">Sell: R{(product.selling_price_incl_vat || product.selling_price || 0).toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedProducts.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-teal-50">
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Cost Price (incl VAT)</TableHead>
                            <TableHead className="text-right">Normal Price</TableHead>
                            <TableHead className="text-right">Promo Price *</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedProducts.map((product, idx) => (
                            <TableRow key={product.id}>
                              <TableCell>
                                <div className="font-medium">{product.description || product.name}</div>
                                <div className="text-xs text-gray-500">PLU: {product.plu_code}</div>
                              </TableCell>
                              <TableCell className="text-right text-gray-500">
                                R{(product.cost_price || product.cost_price_incl_vat || 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right">
                                R{(product.selling_price || product.selling_price_incl_vat || 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <span className="text-gray-500">R</span>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={product.promo_price || ""}
                                    onChange={(e) => {
                                      const updated = selectedProducts.map((p, i) => 
                                        i === idx ? {...p, promo_price: e.target.value} : p
                                      );
                                      setSelectedProducts(updated);
                                    }}
                                    className="w-24 text-right"
                                    placeholder="0.00"
                                    data-testid={`promo-price-${idx}`}
                                  />
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedProducts(selectedProducts.filter((_, i) => i !== idx));
                                    setForm({...form, product_ids: form.product_ids.filter(id => id !== product.id)});
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {selectedProducts.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Search and add products above to set custom promotional prices
                    </p>
                  )}
                </div>
              )}

              {form.promotion_type === "fixed_amount" && (
                <div>
                  <Label>Discount Amount *</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-500">R</span>
                    <Input
                      type="number"
                      min="1"
                      value={form.discount_amount}
                      onChange={(e) => setForm({...form, discount_amount: e.target.value})}
                      className="w-32"
                    />
                    <span className="text-gray-500">off</span>
                  </div>
                </div>
              )}

              {form.promotion_type === "buy_x_get_y" && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Buy Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.buy_quantity}
                      onChange={(e) => setForm({...form, buy_quantity: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Get Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.get_quantity}
                      onChange={(e) => setForm({...form, get_quantity: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Discount on Free Items</Label>
                    <Select 
                      value={form.get_discount_percent}
                      onValueChange={(v) => setForm({...form, get_discount_percent: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">100% (FREE)</SelectItem>
                        <SelectItem value="50">50% Off</SelectItem>
                        <SelectItem value="25">25% Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {form.promotion_type === "multi_buy" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="2"
                      value={form.multi_buy_quantity}
                      onChange={(e) => setForm({...form, multi_buy_quantity: e.target.value})}
                      placeholder="e.g., 3"
                    />
                  </div>
                  <div>
                    <Label>For Price (R)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.multi_buy_price}
                      onChange={(e) => setForm({...form, multi_buy_price: e.target.value})}
                      placeholder="e.g., 100"
                    />
                  </div>
                  <div className="col-span-2 text-sm text-gray-500">
                    Example: "3 for R100" - Customer pays R100 for 3 items
                  </div>
                </div>
              )}

              {form.promotion_type === "spend_reward" && (
                <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="text-sm text-amber-900">
                    <strong>Spend Reward:</strong> Shopper hits a basket
                    threshold, till adds a free voucher line and (with
                    Flash VAS wired up later) issues a printable airtime
                    PIN. Use the <em>Voucher Calculator</em> tab first to
                    check the promo is profitable before saving.
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Basket spend threshold (R)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={form.spend_threshold}
                        onChange={(e) => setForm({ ...form, spend_threshold: e.target.value })}
                        placeholder="500"
                        data-testid="promo-spend-threshold"
                      />
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-700">
                        <input
                          id="promo-thr-vat"
                          type="checkbox"
                          checked={form.threshold_is_vat_inclusive}
                          onChange={(e) => setForm({ ...form, threshold_is_vat_inclusive: e.target.checked })}
                          data-testid="promo-threshold-vat-incl"
                        />
                        <label htmlFor="promo-thr-vat">Threshold is VAT-inclusive</label>
                      </div>
                    </div>
                    <div>
                      <Label>Voucher face value (R)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={form.voucher_value}
                        onChange={(e) => setForm({ ...form, voucher_value: e.target.value })}
                        placeholder="50"
                        data-testid="promo-voucher-value"
                      />
                    </div>
                    <div>
                      <Label>Your cost per voucher (R)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.voucher_cost}
                        onChange={(e) => setForm({ ...form, voucher_cost: e.target.value })}
                        placeholder="48.00"
                        data-testid="promo-voucher-cost"
                      />
                      <div className="text-xs text-slate-600 mt-1">Flash VAS wholesale rate.</div>
                    </div>
                    <div>
                      <Label>Line label on receipt</Label>
                      <Input
                        type="text"
                        value={form.voucher_description}
                        onChange={(e) => setForm({ ...form, voucher_description: e.target.value })}
                        placeholder="FREE R50 Airtime Voucher"
                        data-testid="promo-voucher-desc"
                      />
                    </div>
                    <div>
                      <Label>Voucher expiry (days)</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={form.voucher_expiry_days}
                        onChange={(e) => setForm({ ...form, voucher_expiry_days: e.target.value })}
                        placeholder="30"
                        data-testid="promo-voucher-expiry-days"
                      />
                      <div className="text-xs text-slate-600 mt-1">
                        Number of days from issue date until the voucher expires. Defaults to 30.
                      </div>
                    </div>
                  </div>

                  <hr className="border-amber-200" />

                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-amber-900">
                      Exclude low-margin items from the qualifying basket
                    </div>
                    <div>
                      <Label>Minimum markup %</Label>
                      <Input
                        type="number"
                        step="1"
                        value={form.qualifying_min_markup_percent}
                        onChange={(e) => setForm({ ...form, qualifying_min_markup_percent: e.target.value })}
                        placeholder="20"
                        data-testid="promo-min-markup"
                      />
                      <div className="text-xs text-slate-600 mt-1">
                        Items with markup BELOW this % (e.g. loss-leaders,
                        airtime, cigarettes) don&apos;t count toward the R
                        {form.spend_threshold || "…"} threshold. Leave
                        blank to include everything.
                      </div>
                    </div>

                    <div>
                      <Label>Exclude these categories from qualifying</Label>
                      <div className="max-h-40 overflow-y-auto rounded border border-slate-200 bg-white p-2 space-y-1">
                        {categories.length === 0 && (
                          <div className="text-xs text-slate-500 italic">Loading categories…</div>
                        )}
                        {categories.map((cat) => {
                          const catId = cat.id || cat._id || cat.name;
                          const checked = form.excluded_category_ids?.includes(catId);
                          return (
                            <label key={catId} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={!!checked}
                                onChange={(e) => {
                                  const next = new Set(form.excluded_category_ids || []);
                                  if (e.target.checked) next.add(catId);
                                  else next.delete(catId);
                                  setForm({ ...form, excluded_category_ids: Array.from(next) });
                                }}
                                data-testid={`promo-excl-cat-${catId}`}
                              />
                              {cat.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label>Exclude these stock departments</Label>
                      <div className="max-h-40 overflow-y-auto rounded border border-slate-200 bg-white p-2 space-y-1">
                        {departments.length === 0 && (
                          <div className="text-xs text-slate-500 italic">
                            No departments found (departments are configured under
                            Settings → Categories &amp; Departments).
                          </div>
                        )}
                        {departments.map((dep) => {
                          const depId = dep.id || dep._id || dep.name;
                          const checked = form.excluded_department_ids?.includes(depId);
                          return (
                            <label key={depId} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={!!checked}
                                onChange={(e) => {
                                  const next = new Set(form.excluded_department_ids || []);
                                  if (e.target.checked) next.add(depId);
                                  else next.delete(depId);
                                  setForm({ ...form, excluded_department_ids: Array.from(next) });
                                }}
                                data-testid={`promo-excl-dep-${depId}`}
                              />
                              <span className="text-slate-500 text-xs mr-1">
                                {dep.category_name ? `[${dep.category_name}] ` : ""}
                              </span>
                              {dep.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {form.promotion_type === "bundle" && (
                <div>
                  <Label>Bundle Price (R)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.bundle_price}
                    onChange={(e) => setForm({...form, bundle_price: e.target.value})}
                    className="w-48"
                    placeholder="e.g., 199"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Add products below to include in this bundle
                  </p>
                </div>
              )}
            </div>

            {/* Applies To */}
            <div>
              <Label>Applies To</Label>
              <Select 
                value={form.applies_to}
                onValueChange={(v) => setForm({...form, applies_to: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="category">Specific Categories</SelectItem>
                  <SelectItem value="specific">Specific Products</SelectItem>
                </SelectContent>
              </Select>

              {form.applies_to === "category" && (
                <div className="mt-3">
                  <Label>Select Categories</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                      <Badge
                        key={cat.id}
                        variant={form.category_ids.includes(cat.id) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          if (form.category_ids.includes(cat.id)) {
                            setForm({...form, category_ids: form.category_ids.filter(id => id !== cat.id)});
                          } else {
                            setForm({...form, category_ids: [...form.category_ids, cat.id]});
                          }
                        }}
                      >
                        {cat.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {form.applies_to === "specific" && (
                <div className="mt-3">
                  <Label>Search & Add Products</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name or PLU..."
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        searchProducts(e.target.value);
                      }}
                      className="pl-10"
                    />
                    {products.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {products.map(product => (
                          <div
                            key={product.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                            onClick={() => addProduct(product)}
                          >
                            <span>{product.description}</span>
                            <span className="text-gray-500">{product.plu_code}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {selectedProducts.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {selectedProducts.map(product => (
                        <div key={product.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded">
                          <span>{product.description} ({product.plu_code})</span>
                          <Button variant="ghost" size="sm" onClick={() => removeProduct(product.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({...form, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({...form, end_date: e.target.value})}
                />
              </div>
            </div>

            {/* Active Days */}
            <div>
              <Label>Active Days</Label>
              <div className="flex gap-2 mt-2">
                {WEEKDAYS.map((day, idx) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      if (form.active_days.includes(idx)) {
                        setForm({...form, active_days: form.active_days.filter(d => d !== idx)});
                      } else {
                        setForm({...form, active_days: [...form.active_days, idx].sort()});
                      }
                    }}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                      form.active_days.includes(idx)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {day.charAt(0)}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Range (Happy Hour) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({...form, start_time: e.target.value})}
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({...form, end_time: e.target.value})}
                />
              </div>
            </div>

            {/* Conditions */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Min Purchase (R)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.minimum_purchase}
                  onChange={(e) => setForm({...form, minimum_purchase: e.target.value})}
                  placeholder="No minimum"
                />
              </div>
              <div>
                <Label>Max Total Uses</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.maximum_uses}
                  onChange={(e) => setForm({...form, maximum_uses: e.target.value})}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <Label>Max Per Sale</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_per_transaction}
                  onChange={(e) => setForm({...form, max_per_transaction: e.target.value})}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            {/* Display Options */}
            <div className="space-y-4">
              <div>
                <Label>Shelf Talker Text</Label>
                <Input
                  value={form.shelf_talker_text}
                  onChange={(e) => setForm({...form, shelf_talker_text: e.target.value})}
                  placeholder="e.g., SPECIAL OFFER!"
                />
              </div>
              
              <div>
                <Label>Highlight Color</Label>
                <div className="flex gap-2 mt-2">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setForm({...form, highlight_color: color})}
                      className={`w-8 h-8 rounded-full border-2 ${
                        form.highlight_color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Original Price</Label>
                  <p className="text-sm text-gray-500">Display strikethrough original price on shelf talker</p>
                </div>
                <Switch
                  checked={form.show_original_price}
                  onCheckedChange={(checked) => setForm({...form, show_original_price: checked})}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-sm text-gray-500">Enable or disable this promotion</p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({...form, is_active: checked})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} data-testid="save-promotion-btn">
              {editingPromo ? "Update Promotion" : "Create Promotion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PromoPickerDialog
        open={showPicker}
        onClose={() => setShowPicker(false)}
        promotions={promotions}
        onCreate={(chosen) => {
          resetForm();
          setForm((f) => ({ ...f, promotion_type: "custom_price", applies_to: "specific" }));
          setSelectedProducts(chosen.map((r) => ({ ...r, description: r.name, cost_price: getCostPriceInclVat(r), promo_price: "" })));
          setShowPicker(false);
          setShowModal(true);
          toast.success(`${chosen.length} products added. Enter each promo price, then save.`);
        }}
        onAddToExisting={(promo, chosen) => {
          handleEdit(promo);
          setSelectedProducts((prev) => [...prev, ...chosen.filter((r) => !prev.some((p) => p.id === r.id)).map((r) => ({ ...r, description: r.name, cost_price: getCostPriceInclVat(r), promo_price: "" }))]);
          setShowPicker(false);
          toast.success(`Added to ${promo.name}. Enter the new promo prices, then update.`);
        }}
      />
    </div>
  );
}
